import { useState, useRef, useEffect, type ReactNode, type ButtonHTMLAttributes } from 'react';

/**
 * The primitive set. Built rather than imported: the app needs eight controls,
 * and a component library would ship a second design system alongside the
 * tokens plus a large dependency for the privilege.
 *
 * Everything here draws from the @theme tokens. There are no hex values or
 * magic pixel numbers below this line.
 */

// --- button -----------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON: Record<Variant, string> = {
  primary: 'bg-brass text-page hover:brightness-110 font-medium',
  secondary: 'bg-inset text-ink border border-line hover:bg-hover hover:border-line-strong',
  ghost: 'text-ink-2 hover:text-ink hover:bg-hover',
  danger: 'text-brick hover:bg-brick/10',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1.5 text-xs transition-colors duration-[120ms] ease-out disabled:opacity-40 ${BUTTON[variant]} ${className}`}
    />
  );
}

export function IconButton({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...props}
      className={`inline-flex size-7 shrink-0 items-center justify-center rounded-xs text-ink-3 transition-colors duration-[120ms] ease-out hover:bg-hover hover:text-ink ${className}`}
    />
  );
}

// --- input ------------------------------------------------------------------

export function Input({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-xs border border-line bg-inset px-2.5 py-1.5 text-ink placeholder:text-ink-3 transition-colors duration-[120ms] ease-out hover:border-line-strong focus:border-brass focus:outline-none ${className}`}
    />
  );
}

// --- select -----------------------------------------------------------------

export interface Option {
  value: string;
  label: string;
}

/**
 * Replaces the native <select>, which rendered OS chrome and clipped its own
 * labels in the table ("To dc" for "To do"). Listbox semantics are hand-wired
 * rather than pulled from a headless library — it is one control with four
 * keys of behaviour, and the dependency would not pay for itself.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder,
  align = 'left',
  className = '',
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  align?: 'left' | 'right';
  className?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));

    function onPointerDown(e: PointerEvent) {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, options, value]);

  function choose(index: number) {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className="flex w-full items-center justify-between gap-1.5 rounded-xs border border-transparent px-1.5 py-1 text-left text-xs text-ink transition-colors duration-[120ms] ease-out hover:border-line hover:bg-hover"
      >
        <span className={`truncate ${selected ? '' : 'text-ink-3'}`}>
          {selected?.label ?? placeholder ?? ''}
        </span>
        <Chevron open={open} />
      </button>

      {open && (
        <ul
          role="listbox"
          className={`absolute z-50 mt-1 max-h-64 min-w-full overflow-y-auto rounded-sm border border-line bg-overlay py-1 shadow-lg shadow-black/40 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {options.map((option, i) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(i)}
                className={`flex w-full items-center gap-2 whitespace-nowrap px-2.5 py-1.5 text-left text-xs transition-colors duration-[120ms] ease-out ${
                  i === active ? 'bg-hover text-ink' : 'text-ink-2'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`size-1 shrink-0 rounded-full ${
                    option.value === value ? 'bg-brass' : 'bg-transparent'
                  }`}
                />
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 10 6"
      className={`size-2 shrink-0 text-ink-3 transition-transform duration-[120ms] ease-out ${open ? 'rotate-180' : ''}`}
    >
      <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// --- checkbox ---------------------------------------------------------------

/**
 * The most-clicked control in the app, and on a phone the one most likely to
 * be missed. The visual box stays 16px for density; the hit area is padded out
 * past 44px so touch accuracy does not pay for it.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  className = '',
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`group -m-3 inline-flex shrink-0 items-center justify-center p-3 ${className}`}
    >
      <span
        className={`flex size-4 items-center justify-center rounded-xs border transition-all duration-[120ms] ease-out ${
          checked
            ? 'border-brass bg-brass'
            : 'border-line-strong bg-transparent group-hover:border-brass group-hover:bg-brass/10'
        }`}
      >
        <svg
          viewBox="0 0 12 12"
          aria-hidden="true"
          className={`size-3 text-page transition-opacity duration-[120ms] ease-out ${checked ? 'opacity-100' : 'opacity-0'}`}
        >
          <path
            d="M2.5 6.2l2.4 2.4L9.6 3.9"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}

// --- chip -------------------------------------------------------------------

export function Chip({
  active = false,
  onClick,
  children,
  className = '',
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const base =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors duration-[120ms] ease-out';
  const tone = active
    ? 'bg-brass text-page'
    : 'bg-inset text-ink-2 border border-line hover:border-line-strong hover:text-ink';

  if (!onClick) return <span className={`${base} ${tone} ${className}`}>{children}</span>;

  return (
    <button type="button" aria-pressed={active} onClick={onClick} className={`${base} ${tone} ${className}`}>
      {children}
    </button>
  );
}

/** Non-interactive metadata, e.g. a tag or project name on a row. */
export function Tag({ children }: { children: ReactNode }) {
  return <span className="t-meta whitespace-nowrap text-ink-3">{children}</span>;
}

// --- empty state ------------------------------------------------------------

/**
 * An empty screen is an invitation, not a status report — so every one of
 * these says what the screen is for and what to do next, and is anchored by a
 * mark so a healthy week does not look like a broken page.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-md border border-dashed border-line px-5 py-8">
      <span aria-hidden="true" className="mb-1 block h-px w-8 bg-brass-dim" />
      <p className="text-ink">{title}</p>
      <p className="t-meta max-w-prose">{body}</p>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

// --- page header ------------------------------------------------------------

/**
 * Every view gets the same header shape so the current one is unmistakable:
 * a display-face title, a rule that runs to the edge of the measure, and an
 * optional count and actions on the baseline.
 */
export function PageHeader({
  title,
  subtitle,
  count,
  actions,
}: {
  title: string;
  subtitle?: string;
  count?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <h1 className="t-title text-ink">{title}</h1>
        {count && (
          <span className="t-meta text-ink-3" data-numeric>
            {count}
          </span>
        )}
        <span aria-hidden="true" className="h-px min-w-8 flex-1 self-center bg-line" />
        {actions}
      </div>
      {subtitle && <p className="t-meta mt-1.5">{subtitle}</p>}
    </header>
  );
}

// --- section label ----------------------------------------------------------

export function SectionLabel({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <div className="mb-2 flex items-baseline gap-2">
      <h2 className="t-label">{children}</h2>
      {count !== undefined && count > 0 && (
        <span className="t-meta text-ink-3" data-numeric>
          {count}
        </span>
      )}
    </div>
  );
}
